<template>
  <li
    :id="message.key"
    class="octadesk-chat-item"
    :class="[
      {
        'octadesk-chat-self': isClientMessage,
        'octadesk-chat-other': !isClientMessage
      },
      messageClass
    ]"
  >
    <ConversationMessageThumb
      v-if="showThumb"
      :user="message.user"
    ></ConversationMessageThumb>

    <div
      class="octadesk-chat-msg layout-relative"
      :style="style"
      :class="[
        { 'focused-msg': focusedMessageKey === message.key },
        { last: !nextMessage },
        { 'octadesk-chat-msg-quote': message.quotedMessage },
        message.attachments.length ? 'attachments msg-width-with-attach' : '',
        {
          'cursor-default': inBotFlux
        }
      ]"
      @dblclick="!inBotFlux ? quoteMessage(message) : null"
    >
      <QuoteButton v-if="!inBotFlux" :message="message" />

      <ConversationQuote
        v-if="message.quotedMessage && !inBotFlux"
        :quote="message.quotedMessage"
        :sended="true"
      />

      <ConversationLink
        v-if="hasLink"
        :link="{ label: message.comment, href: bot.link }"
      />

      <ConversationComment
        v-if="message.comment.length && !hasLink"
        :comments="[message.comment]"
        :class="[message.quotedMessage ? 'reply' : '']"
      ></ConversationComment>

      <template v-if="message.attachments.length">
        <ConversationMessageAttachment
          v-for="attachment in message.attachments"
          :key="attachment.url"
          :attachment="attachment"
        ></ConversationMessageAttachment>
      </template>

      <div
        class="woz-memories-btn"
        :style="{ '--woz-memories-color': `rgba(${settings.bgColorRGB})` }"
      >
        <button v-if="canSeeRelatedMemories" @click="emitSeeMemories">
          {{ $t('seeMemories') }}
        </button>
      </div>
    </div>

    <header v-if="showName" class="octadesk-chat-name">
      <span v-if="isClientMessage" class="msg--states--container">
        <span class="sender--time">{{ message.time | timeFormat }}</span>

        <span v-if="isClientMessage" class="msg-state">
          <a
            v-if="message.status === 'error'"
            :tooltip="$t('conversation.message.sendAgain')"
            class="icon icon-msg-error msg-error"
            @click="retrySendMessage(message)"
          ></a>

          <i
            v-else-if="message.agentRead"
            v-tooltip="
              `${$t('conversation.message.viewed')} ${DateVisualization} ${$t(
                'conversation.message.at'
              )}: ${this.$options.filters.timeFormat(message.agentRead)}`
            "
            class="icon icon-msg-received msg-read"
          ></i>

          <i v-else class="icon" :class="msgStateIcon"></i>
        </span>
      </span>

      <span v-else class="msg--states--container">
        <span class="sender--name">
          {{ displayName() || message.user.displayName || message.user.name }}
        </span>

        <span class="sender--time">{{ message.time | timeFormat }}</span>
      </span>
    </header>

    <ConversationButtons
      v-if="showButtons"
      ref="conversationButtonRef"
      :message="message"
    />
  </li>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  import dayjs from 'dayjs'

  import filters from '@/filters'

  import { isClient } from '@/helpers'

  import ConversationMessageThumb from './ConversationMessageThumb'

  import ConversationMessageAttachment from './ConversationMessageAttachment'

  import ConversationComment from './ConversationComment'

  import QuoteButton from './QuoteButton'

  import ConversationQuote from '../../ConversationQuote'

  import ConversationButtons from './button/ConversationButtons.vue'

  import ConversationLink from './ConversationLink.vue'

  import services from '@/services'

  export default {
    components: {
      ConversationMessageThumb,
      ConversationMessageAttachment,
      ConversationComment,
      ConversationQuote,
      QuoteButton,
      ConversationButtons,
      ConversationLink
    },
    props: ['value'],
    computed: {
      ...mapGetters([
        'conversationMessages',
        'user',
        'settings',
        'focusedMessageKey',
        'inBotFlux',
        'agent',
        'isWozTestAgent'
      ]),
      canSeeRelatedMemories() {
        return (
          this.isWozTestAgent &&
          this.message.origin === 'WOZ' &&
          this.message.metadata.woz.correlationId &&
          this.message.metadata.woz.memoryIds.length > 0
        )
      },
      currentAgent() {
        if (this.agent) {
          return this.agent
        } else {
          return (
            this.inBotFlux && this.firstBotMessage && this.firstBotMessage.user
          )
        }
      },
      msgStateIcon() {
        if (this.message.status === 'sending') {
          return 'icon-msg-sending'
        } else if (this.message.status === 'sended') {
          return 'icon-msg-sent'
        } else {
          return 'icon-msg-received'
        }
      },
      DateVisualization() {
        const now = dayjs()

        const lastMsgTime = this.message.agentRead

        const lastMsg = dayjs(lastMsgTime)

        const same = lastMsg.isSame(now, 'day')

        if (same) {
          return this.$t('conversation.message.today')
        } else {
          return `${lastMsg.locale('pt-br').format('DD MMM, h a')}`
        }
      },
      messageClass() {
        const classes = []

        if (
          (!this.isMyMessage(this.previousMessage) &&
            this.isMyMessage(this.nextMessage)) ||
          this.messagePassInterval(this.message, this.previousMessage)
        ) {
          classes.push('first')
        }

        if (
          (!this.isMyMessage(this.nextMessage) &&
            this.isMyMessage(this.previousMessage)) ||
          this.messagePassInterval(this.nextMessage, this.message)
        ) {
          classes.push('last')
        }

        if (
          this.isMyMessage(this.nextMessage) &&
          this.isMyMessage(this.previousMessage)
        ) {
          classes.push('medium')
        }

        return classes.join(' ')
      },
      style() {
        if (this.isClientMessage) {
          let newRBGAColor = this.settings.bgColorRGBA.split(',')

          newRBGAColor.pop()

          newRBGAColor.push(' 1')

          if (this.showName) {
            return `background-color: rgba(${newRBGAColor}); color: rgba(${this.settings.fontColorRGBA}); margin-bottom: 1.5rem;`
          }
          return `background-color: rgba(${newRBGAColor}); color: rgba(${this.settings.fontColorRGBA});`
        } else {
          return 'background-color: #F7F7F7'
        }
      },
      message() {
        return this.conversationMessages.find(m => m.key === this.value.key)
      },
      nextMessage() {
        return this.conversationMessages[this.value.index + 1]
      },
      previousMessage() {
        return this.conversationMessages[this.value.index - 1]
      },
      showThumb() {
        return !this.isMyMessage(this.nextMessage)
      },
      showName() {
        return !this.isMyMessage(this.nextMessage)
      },
      isClientMessage() {
        return isClient(this.message.user.type)
      },
      isAgentMessage() {
        return !isClient(this.message.user.type)
      },
      bot() {
        return (
          this.message &&
          this.message.extendedData &&
          this.message.extendedData.bot
        )
      },
      hasButtons() {
        return this.bot && this.bot.buttons && this.bot.buttons.length > 0
      },
      hasLink() {
        return this.bot && this.bot.link
      },
      hasRepliedMessage() {
        const indexOfCurrent = this.value.index

        const tail = this.conversationMessages.slice(indexOfCurrent + 1)

        return tail.some(item => isClient(item.user.type))
      },
      showButtons() {
        return this.hasButtons && !this.hasRepliedMessage
      }
    },
    methods: {
      ...mapActions(['retrySendMessage', 'quoteMessage']),
      isMyMessage(message) {
        if (message) {
          return message.user.id
            ? message.user.id === this.message.user.id
            : message.user.email === this.message.user.email
        }

        return false
      },
      messagePassInterval(actual, previous) {
        if (actual && previous) {
          return filters.DateFilters.diff(previous.time, actual.time, 'm') >= 2
        }
        return false
      },
      displayName() {
        if (
          this.currentAgent !== undefined &&
          this.currentAgent.displayName !== undefined
        ) {
          return this.currentAgent.displayName
        }
      },
      emitSeeMemories() {
        services.iframe.postMessage({
          name: 'wozMemories',
          correlationId: this.message.metadata.woz.correlationId,
          memoryIds: this.message.metadata.woz.memoryIds
        })
      }
    }
  }
</script>

<style scoped>
  .octadesk-chat-msg {
    cursor: pointer;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
  }

  .cursor-default {
    cursor: default !important;
  }

  .focused-msg {
    box-shadow: inset 1px 2px 21px 6px #aaa !important;
    transition: box-shadow 0.3s ease-in-out;
  }

  .octadesk-chat-other .octadesk-chat-msg {
    float: none !important;
  }

  .octadesk-chat-self .octadesk-chat-msg {
    float: right;
    right: 0px;
    border-radius: 29px 29px 8px 29px;
  }

  .octadesk-chat-other .octadesk-chat-msg {
    border-top-right-radius: 29px;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 29px;
    border-bottom-right-radius: 29px;
  }

  .msg-read {
    color: #1583fe !important;
  }

  .msg-error {
    color: #ee2626 !important;
  }

  .msg-error:hover {
    cursor: pointer;
  }

  .msg-state {
    color: #999999;
    margin-left: 4px;
    font-size: 12px;
  }

  .pre span {
    white-space: normal !important;
  }

  .octadesk-chat-self > .octadesk-chat-msg {
    padding-left: 0.95rem;
    text-align: right;
    max-width: calc(100% - 1.5rem);
  }

  .octadesk-chat-self.last .octadesk-chat-msg {
    border-top-right-radius: 8px !important;
  }

  .octadesk-chat-self.medium .octadesk-chat-msg {
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
  }

  .octadesk-chat-self.first .octadesk-chat-msg {
    border-top-right-radius: 29px !important;
    border-bottom-right-radius: 8px !important;
  }

  .octadesk-chat-other.last .octadesk-chat-msg {
    border-top-left-radius: 8px !important;
    border-bottom-left-radius: 29px !important;
  }

  .octadesk-chat-other.medium .octadesk-chat-msg {
    border-top-left-radius: 8px !important;
    border-bottom-left-radius: 8px !important;
  }

  .octadesk-chat-other.first .octadesk-chat-msg {
    border-bottom-left-radius: 8px !important;
  }

  .octadesk-chat-item {
    margin-bottom: -1px;
    position: relative;
  }

  .octadesk-chat-other .octadesk-chat-name {
    margin-top: 0.4375rem;
  }

  .octadesk-chat-self .octadesk-chat-name {
    position: absolute;
    right: 0;
    bottom: 4px;
  }

  .octadesk-chat-typing .octadesk-chat-name,
  .octadesk-chat-other .octadesk-chat-name {
    padding-left: 3.125rem;
  }

  .octadesk-chat-typing .octadesk-app-avatar,
  .octadesk-chat-other .octadesk-app-avatar {
    order: 1;
    margin-right: 0.3125rem;
    float: left;
  }

  .octadesk-chat-typing .octadesk-chat-msg,
  .octadesk-chat-other .octadesk-chat-msg {
    order: 2;
    margin-left: 2.1875rem;
    padding-left: 0.95rem;
    float: left;
  }

  .octadesk-chat-other .octadesk-chat-msg a,
  .octadesk-chat-self .octadesk-chat-msg a {
    color: inherit;
  }

  .octadesk-chat-other {
    margin-bottom: 4px;
  }

  .octadesk-chat-msg {
    max-width: calc(100% - 2.5rem);
    display: inline-block;
    position: relative;
    background: #ddd;
    padding: 0.55rem 0.625rem calc(0.55rem + 5px) 0.625rem;
    padding-right: 2rem !important;
    border-radius: 0.94rem;
    font-size: 0.875rem;
    line-height: 1.3;
    word-break: break-word;
  }

  /*  */

  .octadesk-chat-self.first .octadesk-chat-msg-quote {
    border-top-right-radius: 10px !important;
  }

  .octadesk-chat-self .octadesk-chat-msg-quote {
    padding: 0.55rem 1.55rem 0.8rem 0.55rem !important;
    border-top-left-radius: 10px;
  }

  /*  */

  .sender--name {
    font-family: Roboto;
    margin-right: 6px;
    font-size: 10px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #484848;
    display: inline-block;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sender--time {
    opacity: 0.39;
    font-family: Roboto;
    font-size: 9px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #484848;
  }

  .msg--states--container {
    display: flex;
    align-items: flex-start;
  }

  .octadesk-chat-msg.attachments {
    padding-right: 0.95rem !important;
  }

  .msg-width-with-attach {
    max-width: calc(100% - 2.5rem) !important;
  }

  .pre {
    white-space: pre-line;
  }

  .woz-memories-btn {
    width: 100%;
    display: flex;
    margin: 15px 0px 0px 0px;
    justify-content: flex-end;
  }
  .woz-memories-btn button {
    color: var(--woz-memories-color);
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    font-size: 10px;
    font-weight: bold;
    border: 1px solid var(--woz-memories-color);
  }
  .woz-memories-btn button:hover {
    background-color: var(--woz-memories-color);
    color: white;
  }
</style>
